import React, { useState, useEffect } from 'react';
import Layout from '../layout/Layout';
import axios from '../common/axios';
import dayjs from 'dayjs';
import { Dialog } from '@progress/kendo-react-dialogs';
import Swal from 'sweetalert2';
import { Grid, GridColumn as Column, GridToolbar } from '@progress/kendo-react-grid';
import { ExcelExport } from '@progress/kendo-react-excel-export';
import { IntlProvider, LocalizationProvider } from "@progress/kendo-react-intl";
import { useTranslation } from 'react-i18next';

const initialDataState = {
  skip: 0,
  take: 10
};

const ContractCheckPage = () => {
  const { t } = useTranslation();
  const [data, setData] = useState({});
  const [visibleDialog, setVisibleDialog] = useState(false);
  const [tableData, setTableData] = useState({});
  const [page, setPage] = React.useState(initialDataState);
  const [pageSizeValue, setPageSizeValue] = React.useState();

  const pageChange = event => {
    const targetEvent = event.targetEvent;
    const take = targetEvent.value === 'All' ? tableData.details.length : event.page.take;
    if (targetEvent.value) {
      setPageSizeValue(targetEvent.value);
    }
    setPage({
      ...event.page,
      take
    });
  };

  const _export = React.useRef(null);
  const excelExport = () => {
    if (_export.current !== null) {
      _export.current.save();
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get('/contractconfirm');
        if (res.data.success) {
          setData(res.data.data);
        }
      } catch (error) {
        console.error(t('alert.serviceError'), error);
      }
    };
    fetchData();
  }, [t]);

  const handleCloseModal = () => {
    setVisibleDialog(false);
  };

  const handleButtonClick = (e) => {
    setTableData({ title: e.계약명, details: e.계약내역 });
    setVisibleDialog(true);
  };

  const handleButtonAPIClick = (v) => {
    Swal.fire({
      title: t('checkContract.modifyRequestTitle'),
      text: t('checkContract.modifyRequestText'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: t('checkContract.confirm'),
      cancelButtonText: t('checkContract.cancel')
    }).then((result) => {
      if (result.isConfirmed) {
        axios.get(`/udpatecontract1?record=${v.record}&project=${v.projectCode}`)
          .then(response => {
            if (response.data.success) {
              Swal.fire({
                title: t('checkContract.successTitle'),
                text: t('checkContract.successText'),
                icon: 'success',
                confirmButtonText: t('checkContract.confirm')
              }).then(() => {
                window.location.href = "/confirmContract";
              });
            } else {
              Swal.fire({
                title: t('checkContract.errorTitle'),
                text: t('checkContract.errorText'),
                icon: 'error',
                confirmButtonText: t('checkContract.confirm')
              });
            }
          })
          .catch(() => {
            Swal.fire({
              title: t('checkContract.errorTitle'),
              text: t('checkContract.errorText'),
              icon: 'error',
              confirmButtonText: t('checkContract.confirm')
            });
          });
      }
    });
  };

  const handleButtonAPIClick2 = (v) => {
    Swal.fire({
      title: t('checkContract.proceedRequestTitle'),
      text: t('checkContract.proceedRequestText'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: t('checkContract.confirm'),
      cancelButtonText: t('checkContract.cancel')
    }).then((result) => {
      if (result.isConfirmed) {
        axios.get(`/udpatecontract2?record=${v.record}&project=${v.projectCode}`)
          .then(response => {
            if (response.data.success) {
              Swal.fire({
                title: t('checkContract.successTitle'),
                text: t('checkContract.proceedSuccessText'),
                icon: 'success',
                confirmButtonText: t('checkContract.confirm')
              }).then(() => {
                window.location.href = "/confirmContract";
              });
            } else {
              Swal.fire({
                title: t('checkContract.errorTitle'),
                text: t('checkContract.errorText'),
                icon: 'error',
                confirmButtonText: t('checkContract.confirm')
              });
            }
          })
          .catch(() => {
            Swal.fire({
              title: t('checkContract.errorTitle'),
              text: t('checkContract.errorText'),
              icon: 'error',
              confirmButtonText: t('checkContract.confirm')
            });
          });
      }
    });
  };

  const handleDownload = (base64String, name) => {
    const byteCharacters = atob(base64String);
    const byteNumbers = new Array(byteCharacters.length);
	for (let i = 0; i < byteCharacters.length; i++) {
		byteNumbers[i] = byteCharacters.charCodeAt(i);
	  }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/octet-stream' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${name}.zip`;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <>
      <div className='app-content flex-column-fluid'>
        <div className='app-container container-xxl'>
          <div className="card mb-5 mb-xl-8">
            <div className="card-header border-0 pt-5">
              <h3 className="card-title align-items-start flex-column">
                <span className="card-label fw-bold fs-3 mb-1">{t('header.newContract')}</span>
                <span className="text-muted mt-1 fw-semibold fs-7">{t('home.newContractReview', { count: data.length })}</span>
              </h3>
            </div>
            <div className="card-body py-3">
              <div className="table-responsive">
                <table className="table table-row-bordered table-row-gray-100 align-middle gs-0 gy-3">
                  <thead>
                    <tr className="fw-bold text-muted">
                      <th className="min-w-120px">{t('checkContract.projectName')}</th>
                      <th className="min-w-120px">{t('checkContract.contractName')}</th>
                      <th className="min-w-120px"></th>
                      <th className="min-w-120px">{t('checkContract.guaranteeInfo')}</th>
                      <th className="min-w-120px">{t('checkContract.contractAmount')}</th>
                      <th className="min-w-140px">{t('checkContract.advanceRate')}</th>
                      <th className="min-w-140px">{t('checkContract.taxRate')}</th>
                      <th className="min-w-140px">{t('checkContract.gridTitle')}</th>
                      <th className="min-w-140px">{t('checkContract.status')}</th>
                      <th className="min-w-140px">{t('checkContract.performanceGuarantee')}</th>
                      <th className="min-w-140px">{t('checkContract.contractDetail')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.length > 0 && data.map((v, index) => (
                      <tr key={index}>
                        <td className="text-dark fw-bold text-hover-primary fs-6">{v.프로젝트명}</td>
                        <td className="text-dark fw-bold text-hover-primary fs-6">{v.계약명}</td>
                        <td>
                          <button className="btn btn-sm btn-light" style={{ padding: 5 }} onClick={() => handleButtonAPIClick(v)}>{t('checkContract.modifyRequest')}</button> <br />
                          <button className="btn btn-sm btn-light" style={{ padding: 5 }} onClick={() => handleButtonAPIClick2(v)}>{t('checkContract.proceedToSign')}</button>
                        </td>
                        <td className="text-dark fw-bold text-hover-primary fs-6">{v["하도급대금 연동여부"]}</td>
                        <td className="text-dark fw-bold text-hover-primary fs-6">{v["계약금액(공급원가)"].toLocaleString()}원</td>
                        <td className="text-dark fw-bold text-hover-primary fs-6">{v["선급금(%)"]}%</td>
                        <td className="text-dark fw-bold text-hover-primary fs-6">{v.부가세율}%</td>
                        <td className="text-dark fw-bold text-hover-primary fs-6">{v["월 지급횟수"]}</td>
                        <td>
                          <span className="text-dark text-hover-primary fs-7 d-block">{(v["현금비율"])}%</span>
                          <span className="text-muted text-hover-primary fs-7 d-block">{v["기업구매카드 비율"]}%</span>
                        </td>
                        <td className="text-dark fw-bold text-hover-primary fs-6">{v["기성지급기한"]}</td>
                        <td className="text-dark fw-bold text-hover-primary fs-6">{v["하도급대금 지급보증금"].toLocaleString()}원</td>
                        <td>
                          <span
                            onClick={(e) => {
                              e.preventDefault();
                              handleButtonClick(v);
                            }}
                            className="btn btn-sm btn-icon btn-bg-light btn-active-color-primary"
                          >
                            <i className="ki-duotone  ki-book-square fs-3">
                              <span className="path1"></span>
                              <span className="path2"></span>
                              <span className="path3"></span>
                            </i>
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
      {visibleDialog &&
        <Dialog title={tableData.title + t('checkContract.contractDetail')} width={1200} onClose={handleCloseModal}>
          <LocalizationProvider language="ko-KR">
            <IntlProvider locale="kr">
              <ExcelExport data={tableData.details} ref={_export}>
                <Grid data={tableData.details.slice(page.skip, page.take + page.skip)} onPageChange={pageChange}
                  skip={page.skip} take={page.take} total={tableData.details.length}
                  pageable={{
                    buttonCount: 4,
                    pageSizes: [5, 10, 15, 'All'],
                    pageSizeValue: pageSizeValue
                  }}
                  style={{ maxHeight: 620 }} >
                  <GridToolbar>
                    <button title={t('checkContract.gridDownload')} className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary" onClick={excelExport}>
                      {t('checkContract.gridDownload')}
                    </button>
                  </GridToolbar>
                  <Column field="li_num" title="Id" width="80px" />
                  <Column field="SecCostCodeLv7" title={t('checkContract.supplyAmount')} width="200px" />
                  <Column field="SecCostCodeLv8" title={t('checkContract.paymentInfo')} width="200px" />
                  <Column field="spocItemName" title={t('checkContract.contractName')} width="200px" />
                  <Column field="spocSpec" title={t('checkContract.contractDetail')} width="250px" />
                </Grid>
              </ExcelExport>
            </IntlProvider>
          </LocalizationProvider>
        </Dialog>
      }
    </>
  );
};

export default ContractCheckPage;
